import { useEffect, useState } from 'react';
import { Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { GitBranch as DecisionsIcon } from 'lucide-react';
import { memoryModuleService } from '../../services/memoryModuleService';
import type { DecisionReconstructionDto } from '../../types/memoryModule';

interface SessionMemoryDecisionsProps {
  sessionId: string;
}

/**
 * Read-only reconstruction of what memory injected into this session (§10A.5).
 * Rule text only — never transcript or identifiers.
 */
export const SessionMemoryDecisions = ({ sessionId }: SessionMemoryDecisionsProps) => {
  const [state, setState] = useState<DecisionReconstructionDto | null>(null);

  useEffect(() => {
    void memoryModuleService
      .getDecisionReconstruction(sessionId)
      .then(setState)
      .catch(() => setState(null));
  }, [sessionId]);

  if (!state || (state.injections.length === 0 && state.rulesAtAsOf.length === 0)) {
    return null;
  }

  const injectedRules = state.rulesAtAsOf.filter((r) => r.wasInjected);
  const shown = injectedRules.length > 0 ? injectedRules : state.rulesAtAsOf.slice(0, 3);

  return (
    <div
      className="flex flex-wrap items-start gap-3 border-b border-border px-6 py-2.5"
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mt-0.5 flex items-center gap-1.5">
              <DecisionsIcon size={18} className="text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground">
                Memory decisions
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>What memory injected into this session, and the rule text that was live then.</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Chip label={`${state.injections.length} injected`} variant="outlined" color="default" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {shown.map((rule) => (
          <span key={`${rule.ruleFamilyId}-${rule.version}`} className="truncate text-xs">
            {rule.wasInjected ? '● ' : ''}
            {rule.ruleText}
            <span className="text-xs text-muted-foreground">
              {' '}
              (v{rule.version}, {rule.status})
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};
